import { useStores } from '../../../stores';
import { useFormik } from 'formik';
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, TextField } from '@mui/material';
import { observer } from 'mobx-react-lite';
import { useTranslation } from "react-i18next";
import { KnowledgeTopic } from '../../../domain/KnowledgeTopic';
import { KnowledgeNote } from '../../../domain/KnowledgeNote';
import DefaultTextArea from '../../../components/TextArea';
import { withLazyMounting } from '../../../components/hoc/LazyDialog';

interface Props {
    isOpen: boolean;
    topic?: KnowledgeTopic;
    note?: KnowledgeNote;
    onClose: () => void;
}

interface FormValues {
    id: number;
    name: string;
    description?: string;
    text: string;
}

const KnowledgeNoteDialog = observer((props: Props) => {
    const { t } = useTranslation();
    const { knowledgeStore } = useStores();

    const initialValues = { name: '' } as FormValues;
    if (props.note) {
        initialValues.id = props.note.id;
        initialValues.name = props.note.name;
        initialValues.description = props.note.description;
        initialValues.text = props.note.text;
    }

    const formik = useFormik({
        enableReinitialize: true,
        initialValues,
        onSubmit: (values, { resetForm }) => {
            if (props.note) {
                knowledgeStore.updateKnowledgeNote(values.id, {
                    name: values.name,
                    description: values.description,
                    text: values.text
                });
            } else {
                knowledgeStore.saveKnowledgeNote({
                    name: values.name,
                    description: values.description,
                    text: values.text,
                    knowledgeTopicId: props.topic!.id
                });
            }
            resetForm();
            props.onClose();
        },
    });

    return (
        <Dialog open={props.isOpen} fullWidth={true} onClose={props.onClose}>
            <form onSubmit={formik.handleSubmit}>
                <DialogTitle>{t("manage_note")}</DialogTitle>
                <DialogContent>
                    <TextField
                        margin="normal"
                        id="name"
                        label={t("name")}
                        type="text"
                        fullWidth
                        variant="standard"
                        value={formik.values.name || ''}
                        onChange={formik.handleChange}
                    />
                    <TextField
                        margin="normal"
                        id="description"
                        label={t("description")}
                        type="text"
                        fullWidth
                        variant="standard"
                        value={formik.values.description || ''}
                        onChange={formik.handleChange}
                    />
                    <DefaultTextArea
                        id="text"
                        placeholder={t("text")}
                        minRows={4}
                        value={formik.values.text || ''}
                        onChange={formik.handleChange}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={props.onClose}>{t("cancel")}</Button>
                    <Button color="primary" type="submit">{t("submit")}</Button>
                </DialogActions>
            </form>
        </Dialog>
    );
});

export default withLazyMounting(KnowledgeNoteDialog);